// /settings/pricing — the business's minimum margin % for store prices
// (client sheet C09, owner decision G-G1).
//
// Owner-only. Margin = (price - cost) / price, both before tax. Below the
// minimum, the Stores panel shows an amber warning on the store's row; the
// price is still saved. Blank = no minimum.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PageContainer, Button, Input, Skeleton, Alert, ErrorBoundary, useToast } from '@bbpos/ui';
import { getSubunitMultiplier } from '@bbpos/utils';
import { useAuthStore } from '@/stores/use-auth-store';
import { tenantService } from '@/services/tenant.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { SaveStatus } from '@/components/SaveStatus';
import { formatPaise } from '@/utils/currency';
import { formatPct, lowestPriceForMargin } from '@/utils/margin';

/** '' → null (no minimum); 0 to 99.99 with up to 2 decimals → the number; else undefined (invalid). */
function parsePct(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return undefined;
  const n = Number(t);
  return n >= 0 && n < 100 ? n : undefined;
}

export function PricingSettingsPage() {
  const profile = useAuthStore((s) => s.profile);
  const tenantId = profile?.tenant?.id ?? null;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currency = useTenantCurrency();

  const [saved, setSaved] = useState<number | null | undefined>(undefined);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    tenantService
      .getPricingSettings(tenantId)
      .then((res) => {
        if (cancelled) return;
        setSaved(res.min_margin_pct);
        setInput(res.min_margin_pct === null ? '' : String(res.min_margin_pct));
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Failed to load the minimum margin');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const value = parsePct(input);
  const valid = value !== undefined;
  const dirty = saved !== undefined && (!valid || value !== saved);
  const canSave = !saving && tenantId !== null && valid && dirty;
  const { dialog: leaveDialog } = useUnsavedChangesGuard(dirty);

  // A worked example at a cost of 100 in the business currency.
  const exampleCost = 100 * getSubunitMultiplier(currency);
  const exampleLowest = valid ? lowestPriceForMargin(exampleCost, value) : null;

  const handleSave = async () => {
    if (!canSave || !tenantId) return;
    setSaving(true);
    try {
      const res = await tenantService.setPricingSettings(tenantId, {
        min_margin_pct: value as number | null,
      });
      setSaved(res.min_margin_pct);
      setInput(res.min_margin_pct === null ? '' : String(res.min_margin_pct));
      // The Stores panel reads the minimum with the product.
      queryClient.invalidateQueries({ queryKey: ['product-master'] });
      toast({
        title: res.min_margin_pct === null ? 'Minimum margin removed' : 'Minimum margin saved',
        variant: 'success',
      });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer
      title="Price margin"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Price margin' },
      ]}
    >
      <ErrorBoundary resetKey="pricing-settings">
        <div className="bg-gradient-to-r from-neutral-700 via-brand-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">How much profit should a price keep?</h1>
          <p className="mt-1.5 text-sm text-white/85 max-w-2xl">
            The margin is the part of the price you keep after the cost, before tax. Set the lowest
            margin you want. A store price that keeps less gets a warning on the product&rsquo;s
            Stores tab.
          </p>
        </div>

        {error && (
          <div className="mb-5">
            <Alert variant="danger" title="Couldn't load the minimum margin" description={error} />
          </div>
        )}

        {loading ? (
          <Skeleton variant="rect" height={160} />
        ) : (
          saved !== undefined && (
            <div className={cn(GLASS_FORM, 'p-5 space-y-5 animate-slide-up')}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                  %
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold text-neutral-900 dark:text-white">Minimum margin</h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 mb-3">
                    We warn when a store price leaves less profit than this. You can still save the
                    price. Leave it blank for no minimum.
                  </p>
                  <div className="max-w-xs">
                    <Input
                      label="Minimum margin %"
                      type="number"
                      min={0}
                      max={99.99}
                      step={0.01}
                      inputMode="decimal"
                      value={input}
                      placeholder="No minimum"
                      onChange={(v: string) => setInput(v)}
                      error={
                        valid ? undefined : 'Enter a number from 0 to 99.99, or leave it blank'
                      }
                      helperText={`Currently ${saved === null ? 'no minimum' : formatPct(saved)}`}
                    />
                  </div>
                  {exampleLowest !== null && value !== null && value !== undefined && (
                    <p
                      className="mt-3 inline-flex items-center rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800 transition-all duration-200 dark:bg-brand-900/20 dark:text-brand-200"
                      data-testid="margin-example"
                    >
                      {`Cost ${formatPaise(exampleCost, currency)} → lowest price ${formatPaise(exampleLowest, currency)} for ${formatPct(value)}`}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <SaveStatus dirty={dirty} />
                <Link
                  to="/settings"
                  className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                >
                  Cancel
                </Link>
                <Button variant="primary" onClick={handleSave} disabled={!canSave}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )
        )}
      </ErrorBoundary>
      {leaveDialog}
    </PageContainer>
  );
}
